import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const Footer = () => {
  const { language } = useLanguage();

  // Multilingual content
  const content = {
    en: {
      links: {
        about: "About",
        contact: "Contact",
        faq: "FAQs",
        privacy: "Privacy Policy"
      },
      copyright: "© 2025 SWIFT Academy",
    },
    ar: {
      links: {
        about: "عننا",
        contact: "اتصل",
        faq: "الأسئلة",
        privacy: "سياسة الخصوصية"
      },
      copyright: "© ٢٠٢٥ سويفت أكاديمي",
    }
  };

  const t = content[language];
  const isRTL = language === 'ar';

  return (
    <footer 
      className={`bg-white text-gray-600 text-center py-6 border-t border-gray-200 relative ${
        isRTL ? 'rtl' : 'ltr'
      }`}
      role="contentinfo"
      aria-label="Footer"
    >
      {/* Enhanced green accent with gradient */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-green-600"></div>

      <div className="container mx-auto px-4 max-w-lg">
        {/* Enhanced mobile-first navigation */}
        <nav 
          className="mb-4"
          aria-label="Footer Navigation"
        >
          <ul className="flex flex-wrap justify-center items-center gap-x-4 gap-y-3">
            <li>
              <Link
                to="/about"
                className="block text-sm md:text-base text-gray-600 hover:text-green-600 
                         transition-colors duration-200 ease-in-out
                         focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 
                         focus:rounded-sm px-1 py-1
                         active:text-green-700 active:scale-95 transform
                         min-h-[44px] flex items-center justify-center
                         font-medium"
                aria-label={`${t.links.about} page`}
              >
                {t.links.about}
              </Link>
            </li>
            
            <li className="text-gray-300 select-none" aria-hidden="true">
              •
            </li>
            
            <li>
              <Link
                to="/contact"
                className="block text-sm md:text-base text-gray-600 hover:text-green-600 
                         transition-colors duration-200 ease-in-out
                         focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 
                         focus:rounded-sm px-1 py-1
                         active:text-green-700 active:scale-95 transform
                         min-h-[44px] flex items-center justify-center
                         font-medium"
                aria-label={`${t.links.contact} page`}
              >
                {t.links.contact}
              </Link>
            </li>
            
            <li className="text-gray-300 select-none" aria-hidden="true">
              •
            </li>
            
            <li>
              <Link
                to="/faq"
                className="block text-sm md:text-base text-gray-600 hover:text-green-600 
                         transition-colors duration-200 ease-in-out
                         focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 
                         focus:rounded-sm px-1 py-1
                         active:text-green-700 active:scale-95 transform
                         min-h-[44px] flex items-center justify-center
                         font-medium"
                aria-label={`${t.links.faq} page`}
              >
                {t.links.faq}
              </Link>
            </li>
            
            <li className="text-gray-300 select-none" aria-hidden="true">
              •
            </li>
            
            <li>
              <Link
                to="/privacy-policy"
                className="block text-sm md:text-base text-gray-600 hover:text-green-600 
                         transition-colors duration-200 ease-in-out
                         focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 
                         focus:rounded-sm px-1 py-1
                         active:text-green-700 active:scale-95 transform
                         min-h-[44px] flex items-center justify-center
                         font-medium"
                aria-label={`${t.links.privacy} page`}
              >
                {t.links.privacy}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Enhanced copyright section */}
        <div className="flex flex-col items-center space-y-2">
          <p className="text-sm text-gray-500 font-medium select-none">
            {t.copyright}
          </p>
        </div>

        {/* Invisible spacer for better scroll experience on mobile */}
        <div className="h-2 sm:h-0" aria-hidden="true"></div>
      </div>

      {/* Enhanced visual feedback for touch interactions */}
      <style jsx>{`
        @media (hover: none) and (pointer: coarse) {
          /* Touch device optimizations */
          footer a {
            -webkit-tap-highlight-color: rgba(34, 197, 94, 0.1);
            tap-highlight-color: rgba(34, 197, 94, 0.1);
          }
        }
        
        /* Improve text rendering on Android */
        footer {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        
        /* RTL support */
        footer.rtl {
          direction: rtl;
        }
        
        /* Safe area support for devices with notches/home indicators */
        @supports (padding: max(0px)) {
          footer {
            padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
          }
        }
        
        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          footer a {
            transition: none;
          }
          
          footer a:active {
            transform: none;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;